import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Login as LoginIcon, Logout as LogoutIcon } from '@mui/icons-material';
import moment from 'moment';
import { getOtaCommissionList } from '../../services/api';
import { DataTable, FormDatePicker, FormSelect, FormTextField } from '../../shared';
import { formatHotelDate, formatHotelDateTimeParts } from '../../utils/dateUtils';
import ChannelImage from '../common/ChannelImage';
import OtaCommissionSummary from './OtaCommissionSummary';
import { detectThemeSync } from '../../utils/themeUtils';

const COMMISSION_COL_SX = (isDark) => ({
  backgroundColor: isDark ? 'rgba(250, 204, 21, 0.16)' : 'rgba(250, 204, 21, 0.22)',
  color: isDark ? '#fef9c3 !important' : '#713f12 !important',
  fontWeight: 800,
  '& .MuiTableSortLabel-root': {
    color: isDark ? '#fef9c3 !important' : '#713f12 !important',
    fontWeight: 800,
    '&.Mui-active, &:hover': {
      color: isDark ? '#fde047 !important' : '#a16207 !important',
    },
    '& .MuiTableSortLabel-icon': {
      color: '#ca8a04 !important',
    },
  },
});

function formatStayDate(value) {
  if (!value || value === '—') return '—';
  const parsed = moment(value);
  return parsed.isValid() ? formatHotelDate(value) : '—';
}

function StayLine({ Icon, iconColor, label, date }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ lineHeight: 1.2 }}>
      <Icon sx={{ fontSize: 14, color: iconColor, flexShrink: 0, opacity: 0.92 }} />
      <Typography
        component="span"
        variant="caption"
        sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.65rem', minWidth: 22 }}
      >
        {label}
      </Typography>
      <Typography component="span" variant="body2" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.78rem' }}>
        {date}
      </Typography>
    </Stack>
  );
}

function OtaStayCell({ row }) {
  const nights = Number(row?.nights || 0);
  const hasNights = Number.isFinite(nights) && nights > 0;
  return (
    <Stack spacing={0.25} sx={{ py: 0.15, minWidth: 155 }}>
      <StayLine
        Icon={LoginIcon}
        iconColor="#4ce1b6"
        label="In"
        date={formatStayDate(row?.check_in_date)}
      />
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <StayLine
          Icon={LogoutIcon}
          iconColor="#60a5fa"
          label="Out"
          date={formatStayDate(row?.check_out_date)}
        />
        {hasNights ? (
          <Typography
            variant="caption"
            sx={{
              ml: 0.25,
              color: '#4ce1b6',
              fontWeight: 800,
              fontSize: '0.62rem',
              whiteSpace: 'nowrap',
            }}
          >
            · {nights}N
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
}

function OtaSourceLabel({ name, size = 22 }) {
  const label = name && name !== '—' ? name : 'OTA';
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <ChannelImage channel={{ name: label }} size={size} />
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.25, color: 'text.primary' }} noWrap>
        {label}
      </Typography>
    </Stack>
  );
}

const DATE_PRESETS = [
  { value: 'all', label: 'All' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'custom', label: 'Custom' },
];

function resolveBookingDateRange(preset, customFrom, customTo) {
  const today = moment();
  if (preset === 'today') {
    const day = today.format('YYYY-MM-DD');
    return { from: day, to: day };
  }
  if (preset === 'yesterday') {
    const day = today.clone().subtract(1, 'day').format('YYYY-MM-DD');
    return { from: day, to: day };
  }
  if (preset === 'this_month') {
    return {
      from: today.clone().startOf('month').format('YYYY-MM-DD'),
      to: today.clone().endOf('month').format('YYYY-MM-DD'),
    };
  }
  if (preset === 'last_month') {
    const lastMonth = today.clone().subtract(1, 'month');
    return {
      from: lastMonth.clone().startOf('month').format('YYYY-MM-DD'),
      to: lastMonth.clone().endOf('month').format('YYYY-MM-DD'),
    };
  }
  if (preset === 'custom' && customFrom && customTo) {
    return { from: customFrom, to: customTo };
  }
  return { from: '', to: '' };
}

function BookingDatePresetFilter({ preset, customFrom, customTo, onChange }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(customFrom ? moment(customFrom) : moment());
  const [draftTo, setDraftTo] = useState(customTo ? moment(customTo) : moment());

  const handlePreset = (value) => {
    if (value === 'custom') {
      setDraftFrom(customFrom ? moment(customFrom) : moment());
      setDraftTo(customTo ? moment(customTo) : moment());
      setDialogOpen(true);
      return;
    }
    onChange({ datePreset: value, dateFrom: '', dateTo: '' });
  };

  const applyCustom = () => {
    if (!draftFrom?.isValid() || !draftTo?.isValid()) return;
    const from = draftFrom.clone();
    const to = draftTo.clone();
    const start = from.isAfter(to) ? to : from;
    const end = from.isAfter(to) ? from : to;
    onChange({
      datePreset: 'custom',
      dateFrom: start.format('YYYY-MM-DD'),
      dateTo: end.format('YYYY-MM-DD'),
    });
    setDialogOpen(false);
  };

  return (
    <>
      <FormSelect
        label=""
        value={preset || 'all'}
        onChange={(e) => handlePreset(e.target.value)}
        displayEmpty
        sx={{ minWidth: 132 }}
      >
        {DATE_PRESETS.map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </FormSelect>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>Custom booking date</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormDatePicker
              label="From"
              value={draftFrom}
              onChange={(value) => setDraftFrom(value)}
            />
            <FormDatePicker
              label="To"
              value={draftTo}
              onChange={(value) => setDraftTo(value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={applyCustom} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const createDefaultFilters = () => ({
  booking_code: '',
  guest: '',
  ota_name: '',
  datePreset: 'all',
  dateFrom: '',
  dateTo: '',
});

function formatMoney(amount, currency = 'INR') {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${currency || 'INR'} ${value.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
}

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export default function OtaCommissionList() {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [otaOptions, setOtaOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(createDefaultFilters);
  const [sortConfig, setSortConfig] = useState({ column: 'booking_date', direction: 'desc' });
  const [pagination, setPagination] = useState({
    page: 0,
    perPage: 10,
    totalRecords: 0,
  });

  const debouncedFilters = useDebouncedValue(filters);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sortColumn = sortConfig.column || 'created_at';
      const sortDirection = sortConfig.direction || 'desc';
      const sortParam = sortDirection === 'desc' ? `-${sortColumn}` : sortColumn;
      const dateRange = resolveBookingDateRange(
        debouncedFilters.datePreset,
        debouncedFilters.dateFrom,
        debouncedFilters.dateTo,
      );
      const params = {
        page: pagination.page + 1,
        per_page: pagination.perPage,
        sort: sortParam,
        ...Object.fromEntries(
          Object.entries({
            booking_code: debouncedFilters.booking_code,
            guest: debouncedFilters.guest,
            ota_name: debouncedFilters.ota_name,
            booking_date_from: dateRange.from,
            booking_date_to: dateRange.to,
          }).filter(([, value]) => value !== ''),
        ),
      };
      const response = await getOtaCommissionList(params);
      if (response && response.Data) {
        setRows(response.Data);
        setSummary(response.summary || null);
        if (Array.isArray(response.ota_options)) {
          setOtaOptions(response.ota_options);
        }
        setPagination((prev) => ({
          ...prev,
          totalRecords: response.total_records || 0,
        }));
      } else {
        setRows([]);
        setSummary(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch OTA commissions');
      setRows([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters, sortConfig, pagination.page, pagination.perPage]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const isDark = detectThemeSync() === 'dark';
  const commissionColSx = useMemo(() => COMMISSION_COL_SX(isDark), [isDark]);

  const columns = useMemo(
    () => [
      {
        field: 'booking_code',
        label: 'Booking ID',
        sortable: true,
        render: (value, row) => {
          if (value && value !== '—' && row.booking_id) {
            return (
              <Link
                to={`/booking/detail/${row.booking_id}/`}
                onClick={(e) => e.stopPropagation()}
                style={{ color: theme.palette.primary.main, fontWeight: 700, textDecoration: 'none' }}
              >
                {value}
              </Link>
            );
          }
          return value || '—';
        },
      },
      {
        field: 'booking_date',
        label: 'Booking Date',
        sortable: true,
        render: (value) => {
          const parts = formatHotelDateTimeParts(value);
          if (!parts) return '—';
          return (
            <Stack spacing={0.15}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {parts.date}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
                {parts.time}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'check_in_date',
        label: 'Stay',
        sortable: true,
        render: (_value, row) => <OtaStayCell row={row} />,
      },
      {
        field: 'guest_name',
        label: 'Name',
        sortable: true,
        render: (value, row) => {
          const mobile = row.guest_mobile && row.guest_mobile !== '—' ? row.guest_mobile : '';
          return (
            <Stack spacing={0.15}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {value || '—'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
                {mobile || '—'}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'ota_name',
        label: 'Booking Source',
        sortable: true,
        render: (value) => <OtaSourceLabel name={value} />,
      },
      {
        field: 'amount',
        label: 'Amount',
        sortable: true,
        align: 'right',
        render: (value, row) => formatMoney(value, row.currency),
      },
      {
        field: 'tcs',
        label: 'TCS',
        sortable: true,
        align: 'right',
        render: (value, row) => formatMoney(value, row.currency),
      },
      {
        field: 'tds',
        label: 'TDS',
        sortable: true,
        align: 'right',
        render: (value, row) => formatMoney(value, row.currency),
      },
      {
        field: 'tax',
        label: 'Tax',
        sortable: true,
        align: 'right',
        render: (value, row) => formatMoney(value, row.currency),
      },
      {
        field: 'commission',
        label: 'Commission',
        sortable: true,
        align: 'right',
        sx: commissionColSx,
        render: (value, row) => (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 900,
              letterSpacing: 0.2,
              color: isDark ? '#fef9c3' : '#713f12',
            }}
          >
            {formatMoney(value, row.currency)}
          </Typography>
        ),
      },
      {
        field: 'total',
        label: 'Total',
        sortable: true,
        align: 'right',
        render: (value, row) => (
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {formatMoney(value, row.currency)}
          </Typography>
        ),
      },
    ],
    [commissionColSx, isDark, theme.palette.primary.main],
  );

  const filterRowCells = columns.map((column) => {
    switch (column.field) {
      case 'booking_code':
        return (
          <FormTextField
            key={column.field}
            size="small"
            label="Booking ID"
            value={filters.booking_code}
            onChange={(e) => handleFilterChange('booking_code', e.target.value)}
            sx={{ minWidth: 120 }}
          />
        );
      case 'booking_date':
        return (
          <BookingDatePresetFilter
            key={column.field}
            preset={filters.datePreset}
            customFrom={filters.dateFrom}
            customTo={filters.dateTo}
            onChange={(next) => {
              setFilters((prev) => ({ ...prev, ...next }));
              setPagination((prev) => ({ ...prev, page: 0 }));
            }}
          />
        );
      case 'guest_name':
        return (
          <FormTextField
            key={column.field}
            size="small"
            label="Name / Number"
            value={filters.guest}
            onChange={(e) => handleFilterChange('guest', e.target.value)}
            sx={{ minWidth: 140 }}
          />
        );
      case 'ota_name':
        return (
          <FormSelect
            key={column.field}
            label=""
            value={filters.ota_name}
            onChange={(e) => handleFilterChange('ota_name', e.target.value)}
            displayEmpty
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All OTAs</MenuItem>
            {otaOptions.map((name) => (
              <MenuItem key={name} value={name}>
                <OtaSourceLabel name={name} size={20} />
              </MenuItem>
            ))}
          </FormSelect>
        );
      default:
        return <div key={column.field} />;
    }
  });

  const filtersActive = Boolean(
    filters.booking_code.trim() ||
      filters.guest.trim() ||
      filters.ota_name.trim() ||
      (filters.datePreset && filters.datePreset !== 'all'),
  );

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <OtaCommissionSummary summary={summary} loading={loading && !summary} filtered={filtersActive} />
      {error && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
          {error}
        </Box>
      )}
      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        pagination={pagination}
        onPageChange={(newPage) => setPagination((prev) => ({ ...prev, page: newPage }))}
        onPerPageChange={(newPerPage) =>
          setPagination((prev) => ({ ...prev, perPage: newPerPage, page: 0 }))
        }
        onSortChange={(nextSort) => {
          setSortConfig(nextSort);
          setPagination((prev) => ({ ...prev, page: 0 }));
        }}
        sortConfig={sortConfig}
        emptyMessage="No OTA commissions found."
        stickyHeader
        filterRow={filterRowCells}
      />
    </Box>
  );
}
